import { GRAMMAR_SLOT_ROLES } from '@repo/config';
import { z } from 'zod';

const grammarPatternSlotSchema = z.object({
  label: z.string().min(1).max(28),
  example: z.string().min(1).max(40),
  role: z.enum(GRAMMAR_SLOT_ROLES),
  isTarget: z.boolean(),
  variants: z.array(z.string().min(1).max(24)).max(4).optional(),
});

const grammarMinimalPairSchema = z.object({
  right: z.string().min(3).max(90),
  wrong: z.string().min(3).max(90),
  differenceRight: z.string().min(1).max(40),
  differenceWrong: z.string().min(1).max(40),
  whyWrong: z.string().min(25).max(160),
});

function normalizeSpacing(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

const grammarParadigmRowSchema = z.object({
  cells: z.array(z.string().min(1).max(60)),
  isContrast: z.boolean().optional(),
});

const grammarParadigmSchema = z
  .object({
    title: z.string().min(1).max(80),
    columns: z.array(z.string().min(1).max(40)).min(2).max(4),
    rows: z.array(grammarParadigmRowSchema).min(2).max(8),
  })
  .refine(
    (paradigm) => paradigm.rows.every((row) => row.cells.length === paradigm.columns.length),
    { message: 'every paradigm row must have exactly one cell per column' },
  );

const grammarPatternInstanceObjectSchema = z.object({
  slots: z.array(grammarPatternSlotSchema).min(2).max(6),
  build: z.string().min(3).max(120),
});

const grammarPatternInstanceSchemaDefinition = grammarPatternInstanceObjectSchema
  .refine(
    (instance) =>
      normalizeSpacing(instance.slots.map(({ example }) => example).join(' ')) ===
      normalizeSpacing(instance.build),
    { message: 'slot examples must compose into build' },
  )
  .refine((instance) => instance.slots.filter(({ isTarget }) => isTarget).length === 1, {
    message: 'exactly one slot must be the target',
  });

export const grammarPatternInstanceSchema: typeof grammarPatternInstanceSchemaDefinition =
  grammarPatternInstanceSchemaDefinition;

const grammarPatternSchemaDefinition = z
  .object({
    slots: z.array(grammarPatternSlotSchema).min(2).max(6),
    build: z.string().min(3).max(120),
    plainRule: z.string().min(20).max(140),
    minimalPair: grammarMinimalPairSchema,
    alsoSeeAs: z.array(z.string().min(3).max(90)).max(3).optional(),
    moreInstances: z.array(grammarPatternInstanceSchema).min(1).max(2).nullish(),
    paradigm: grammarParadigmSchema.optional(),
  })
  .refine(
    (pattern) =>
      normalizeSpacing(pattern.slots.map(({ example }) => example).join(' ')) ===
      normalizeSpacing(pattern.build),
    { message: 'slot examples must compose into build' },
  )
  .refine((pattern) => pattern.slots.filter(({ isTarget }) => isTarget).length === 1, {
    message: 'exactly one slot must be the target',
  })
  .refine(
    (pattern) =>
      pattern.minimalPair.right.includes(pattern.minimalPair.differenceRight) &&
      pattern.minimalPair.wrong.includes(pattern.minimalPair.differenceWrong),
    { message: 'each minimal pair difference must occur in its own sentence' },
  )
  .refine(
    (pattern) =>
      normalizeSpacing(
        pattern.minimalPair.right.replace(pattern.minimalPair.differenceRight, ''),
      ) ===
      normalizeSpacing(pattern.minimalPair.wrong.replace(pattern.minimalPair.differenceWrong, '')),
    { message: 'a minimal pair must differ only inside its declared difference spans' },
  );

export const grammarPatternSchema: typeof grammarPatternSchemaDefinition =
  grammarPatternSchemaDefinition;

export type IGrammarPattern = z.infer<typeof grammarPatternSchema>;
export type IGrammarPatternInstance = z.infer<typeof grammarPatternInstanceSchema>;
